import React from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'];

const TopCustomers = ({ data }) => {
    if (!data || data.length === 0) return null;

    const maxShow = Math.min(data.length, 8);
    const sliced = data.slice(0, maxShow);
    const labels = sliced.map(d => d.customer_name?.split(' ').slice(0, 2).join(' ') || 'Unknown');
    const values = sliced.map(d => d.total_spent);
    const colors = labels.map((_, i) => COLORS[i % COLORS.length]);

    const chartData = {
        labels,
        datasets: [{
            label: 'Spent (KWD)',
            data: values,
            backgroundColor: colors,
            borderRadius: 4,
        }],
    };

    const options = {
        responsive: true,
        indexAxis: 'y',
        plugins: {
            legend: { display: false },
        },
        scales: {
            x: { title: { display: true, text: 'Total Spent (KWD)' } },
        },
    };

    return (
        <div className="card shadow-sm h-100">
            <div className="card-body">
                <h6 className="card-title mb-3">Top Customers by Spend</h6>
                <Bar data={chartData} options={options} />
            </div>
        </div>
    );
};

const CustomerTypeBreakdown = ({ data }) => {
    if (!data || data.length === 0) return null;

    const labels = data.map(d => d.label);
    const values = data.map(d => d.count);
    const colors = ['#3B82F6', '#F59E0B'];

    const chartData = {
        labels,
        datasets: [{
            data: values,
            backgroundColor: colors,
            borderWidth: 1,
        }],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 12, padding: 8 } },
        },
    };

    const total = values.reduce((s, v) => s + v, 0);

    return (
        <div className="card shadow-sm h-100">
            <div className="card-body">
                <h6 className="card-title mb-3">Customer Types</h6>
                <Doughnut data={chartData} options={options} />
                <div className="mt-2 text-center text-muted" style={{ fontSize: '0.8rem' }}>
                    Total Customers: {total}
                </div>
            </div>
        </div>
    );
};

const NewCustomersTrend = ({ data }) => {
    if (!data || !data.daily || data.daily.length === 0) return null;

    const sliced = data.daily;
    const labels = sliced.map((d, i) => (i + 1).toString());
    const values = sliced.map(d => d.count);

    const chartData = {
        labels,
        datasets: [{
            label: 'New Customers',
            data: values,
            backgroundColor: 'rgba(139,92,246,0.6)',
            borderRadius: 2,
        }],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { display: false },
        },
        scales: {
            x: { title: { display: true, text: 'Day of Month' } },
            y: { title: { display: true, text: 'Customers' } },
        },
    };

    return (
        <div className="card shadow-sm h-100">
            <div className="card-body">
                <h6 className="card-title mb-3">New Customers This Month</h6>
                <strong className="text-primary" style={{ fontSize: '1.5rem' }}>
                    {data.total_new_customers ?? 0}
                </strong>
                <span className="text-muted ms-2">total new</span>
                <div className="mt-2">
                    <Bar data={chartData} options={options} />
                </div>
            </div>
        </div>
    );
};

const CustomerCharts = ({ topCustomers, customerTypeBreakdown, newCustomersTrend }) => {
    if (!topCustomers && !customerTypeBreakdown && !newCustomersTrend) return null;

    return (
        <div className="mb-4">
            <h5 className="mb-3 border-bottom pb-2">Customer Analysis</h5>
            <div className="row g-3">
                <div className="col-md-5">
                    <TopCustomers data={topCustomers} />
                </div>
                <div className="col-md-3">
                    <CustomerTypeBreakdown data={customerTypeBreakdown} />
                </div>
                <div className="col-md-4">
                    <NewCustomersTrend data={newCustomersTrend} />
                </div>
            </div>
        </div>
    );
};

export default CustomerCharts;
